/**
 * B1021 (c) — gift cards as a tender on the Pay step's PENDING order.
 *
 *   APPLY   number + PIN judged here first (16 digits, Luhn, a 4-digit PIN —
 *           nothing reaches the network for a typo), then the container's
 *           `beforeApply` (the buyer attaches first: a card that covers the
 *           rest CONFIRMS the order there and then, so the tickets need their
 *           name and address before it), then `giftcard.redeem`. A card that
 *           closes the total hands the order to the poller (`onConfirmed`).
 *   REMOVE  `giftcard.unapply` — the hold goes back to the card.
 *
 * The card door's refusals are buyer sentences by construction (one sentence
 * for an unknown number or a wrong PIN, the lock, frozen, used up, twice), so
 * a 409/429 lands at the number field as the server wrote it; anything else
 * goes through the page's translation. A new order starts with no cards —
 * then (B1021 leg 2) the order is read once: the cards it still HOLDS come
 * back (a reload, a resumed order), merged with anything applied meanwhile.
 * A failed read leaves the page as it is; APPLY and REMOVE still work.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api.js";
import { translateRefusal } from "../lib/refusals.js";
import { appliedFromOrder, giftCardNumberProblem, giftCardPinProblem, mergeApplied, type AppliedGiftCard } from "../lib/giftCardTender.js";
import type { Money } from "../lib/money.js";
import type { GiftCardTenderFace } from "./cartPay.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>, opts?: { readonly clientKey?: string }): Promise<unknown>;
}

const UNKNOWN = "That didn’t go through — try again in a moment.";

function refusalOf(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409 || error.status === 429) return error.message;
    return translateRefusal(error);
  }
  return UNKNOWN;
}

export function useGiftCardTender(opts: {
  readonly caller: Caller;
  /** The PENDING order the cards pay on; absent = no tender face. */
  readonly orderId: string | undefined;
  /** Attach the buyer before a card may close the order; false = stop (the container showed why). */
  readonly beforeApply: () => Promise<boolean>;
  /** A card covered the rest: the order confirmed at the door. */
  readonly onConfirmed: () => void;
}): { readonly face: GiftCardTenderFace | undefined; readonly applied: readonly AppliedGiftCard[] } {
  const { caller, orderId, beforeApply, onConfirmed } = opts;
  const [applied, setApplied] = useState<readonly AppliedGiftCard[]>([]);
  const [numberRefusal, setNumberRefusal] = useState<string | undefined>(undefined);
  const [pinRefusal, setPinRefusal] = useState<string | undefined>(undefined);
  const [applying, setApplying] = useState(false);
  const [removing, setRemoving] = useState<string | undefined>(undefined);
  const presses = useRef(0);

  useEffect(() => {
    setApplied([]);
    setNumberRefusal(undefined);
    setPinRefusal(undefined);
    if (orderId === undefined) return;
    let live = true;
    void (async () => {
      try {
        const res = (await caller.call("order.get", { orderId })) as { order?: unknown };
        const held = appliedFromOrder(res.order);
        if (live && held.length > 0) setApplied((onPage) => mergeApplied(held, onPage));
      } catch {
        /* the read failed: the page stands as it is — APPLY and REMOVE still work */
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one read per order; the caller is stable per session
  }, [orderId]);

  const onApply = useCallback(
    (number: string, pin: string) => {
      if (orderId === undefined || applying) return;
      const numberWhy = giftCardNumberProblem(number);
      const pinWhy = giftCardPinProblem(pin);
      setNumberRefusal(numberWhy);
      setPinRefusal(pinWhy);
      if (numberWhy !== undefined || pinWhy !== undefined) return;
      setApplying(true);
      void (async () => {
        try {
          if (!(await beforeApply())) return;
          presses.current += 1;
          const res = (await caller.call(
            "giftcard.redeem",
            { orderId, number: number.trim(), pin: pin.trim() },
            { clientKey: `redeem-${orderId}-${String(presses.current)}` },
          )) as { intentId: string; lastFour: string; amount: Money; left: Money; orderState: "PENDING_PAYMENT" | "CONFIRMED" };
          setApplied((prev) => [...prev, { intentId: res.intentId, lastFour: res.lastFour, amount: res.amount, left: res.left }]);
          if (res.orderState === "CONFIRMED") onConfirmed();
        } catch (error) {
          setNumberRefusal(refusalOf(error));
        } finally {
          setApplying(false);
        }
      })();
    },
    [orderId, applying, beforeApply, caller, onConfirmed],
  );

  const onRemove = useCallback(
    (intentId: string) => {
      if (orderId === undefined || removing !== undefined) return;
      setRemoving(intentId);
      void (async () => {
        try {
          await caller.call("giftcard.unapply", { orderId, intentId }, { clientKey: `unapply-${intentId}` });
          setApplied((prev) => prev.filter((c) => c.intentId !== intentId));
          setNumberRefusal(undefined);
        } catch (error) {
          setNumberRefusal(refusalOf(error));
        } finally {
          setRemoving(undefined);
        }
      })();
    },
    [orderId, removing, caller],
  );

  if (orderId === undefined) return { face: undefined, applied: [] };
  return {
    face: { cards: applied, numberRefusal, pinRefusal, applying, removing, onApply, onRemove },
    applied,
  };
}
